"use client";

import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { useRouter } from "next/navigation";
import { safeReload } from '@/app/_module/utils/safeReload';
import { Building2, Lock, Users, Crown, Check, X } from "lucide-react";
import { useState } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { Badge } from "@/app/_module/components/ui/badge";
import AgencyCreationStep from "@/app/auth/sign-up/_modules/components/AgencyCreationStep";
import { AGENCY_STATUS_QUERY_KEY } from "@/app/_module/hooks/useAgencyStatus";
import { useToast } from "@/app/_module/hooks/use-toast";
import agencyService from "@/app/_module/services/agency-service";

export type UserScenario =
  | 'basic_in_agency'           // Agent with basic role in agency
  | 'full_fledged_in_agency'    // Agent with full-fledged role in agency
  | 'basic_independent'         // Independent basic user
  | 'full_fledged_independent'  // Independent full-fledged user
  | 'owner_of_other_agency';    // Owns an agency elsewhere, agent in the current one

interface SubscriptionGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName: string;
  userScenario: UserScenario;
  accountType: 'trial' | 'lite' | 'basic' | 'full_fledged' | 'enterprise' | 'super_agent';
  hasAgency: boolean;
  currentAgencyId?: string;
}

export default function SubscriptionGateModal({
  isOpen,
  onClose,
  featureName,
  userScenario,
  accountType,
  hasAgency,
  currentAgencyId,
}: SubscriptionGateModalProps) {
  // "trial" alone says nothing — name the trial (Lite / Basic / Full-Fledged),
  // read off the user record since callers only pass the bare account type.
  const { user: gateUser } = useUserContext();
  const PLAN_NAMES: Record<string, string> = { lite: 'Lite', basic: 'Basic', full_fledged: 'Full-Fledged', enterprise: 'Enterprise', super_agent: 'Super Agent' };
  const gu: any = (gateUser as any)?.user || {};
  const trialOf = String(gu.trialAccountTypeUsed || gu.selectedPlan || 'basic').toLowerCase();
  const currentPlanLabel =
    accountType === 'trial'
      ? `${PLAN_NAMES[trialOf] || 'Basic'} Trial`
      : (PLAN_NAMES[accountType] || String(accountType).replace('_', ' '));
  const router = useRouter();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showAgencyCreation, setShowAgencyCreation] = useState(false);
  const [isCreatingAgency, setIsCreatingAgency] = useState(false);

  // Determine modal content based on user scenario
  const getModalContent = () => {
    switch (userScenario) {
      case 'basic_in_agency':
        return {
          icon: <Lock className="h-12 w-12 text-purple-600" />,
          iconBg: 'bg-purple-100',
          title: 'Upgrade to Full-Fledged to Start Your Own Agency',
          description: `To access ${featureName} and create your own sub-agency under your current agency, you need to upgrade to Full-Fledged.`,
          benefits: [
            'Create your own sub-agency',
            'Manage your own team of agents',
            'Access advanced lead assignment',
            'Full agency dashboard access',
            'Advanced analytics and reporting',
          ],
          primaryAction: 'Upgrade to Full-Fledged',
          primaryActionFn: () => {
            onClose();
            // Native app: pop the web-version sheet instead of routing to the
            // (web-only) subscription tab — one tap, not a dead-end hop.
            if (!allowCheckout('subscription')) return;
            router.push('/settings?tab=subscription');
          },
          secondaryAction: 'Cancel',
          showAgencyCreationFlow: false,
        };

      case 'full_fledged_in_agency':
        return {
          icon: <Users className="h-12 w-12 text-green-600" />,
          iconBg: 'bg-green-100',
          title: 'Create Your Sub-Agency',
          description: `You have Full-Fledged access! Create your own sub-agency under your current agency to access ${featureName}.`,
          benefits: [
            'Your own agency under the parent',
            'Manage your own team of agents',
            'Independent lead management',
            'Full dashboard access',
            'Agency-level analytics',
          ],
          primaryAction: 'Create Sub-Agency',
          primaryActionFn: () => setShowAgencyCreation(true),
          secondaryAction: 'Not Now',
          showAgencyCreationFlow: true,
        };

      case 'owner_of_other_agency':
        // MULTI-AGENCY (2026-07-23): they already OWN an agency — the answer
        // isn't "create one", it's "get into your owner agency's context".
        return {
          icon: <Users className="h-12 w-12 text-blue-600" />,
          iconBg: 'bg-blue-100',
          title: 'Switch to Your Own Agency',
          description: `You're an agent in this agency, so ${featureName} is managed by its owners. You already own your own agency — log out and log back in, then select your owner agency to manage it there.`,
          benefits: [
            'Log out, log back in, and select your owner agency',
            `${featureName} is available inside your own agency`,
            'Create sub-agencies from the Agency Dashboard within your own agency',
          ],
          primaryAction: 'Got It',
          primaryActionFn: () => onClose(),
          secondaryAction: 'Close',
          showAgencyCreationFlow: false,
        };

      case 'basic_independent':
        return {
          icon: <Lock className="h-12 w-12 text-purple-600" />,
          iconBg: 'bg-purple-100',
          title: 'Upgrade to Full-Fledged',
          description: `To access ${featureName} and create your own agency, you need to upgrade to Full-Fledged.`,
          benefits: [
            'Create and manage your own agency',
            'Hire and manage agents',
            'Advanced lead assignment tools',
            'Full agency dashboard',
            'Comprehensive analytics',
          ],
          primaryAction: 'Upgrade to Full-Fledged',
          primaryActionFn: () => {
            onClose();
            // Native app: pop the web-version sheet instead of routing to the
            // (web-only) subscription tab — one tap, not a dead-end hop.
            if (!allowCheckout('subscription')) return;
            router.push('/settings?tab=subscription');
          },
          secondaryAction: 'Cancel',
          showAgencyCreationFlow: false,
        };

      case 'full_fledged_independent':
        return {
          icon: <Building2 className="h-12 w-12 text-blue-600" />,
          iconBg: 'bg-blue-100',
          title: 'Create Your Agency',
          description: `You have Full-Fledged access! Create your agency to unlock ${featureName} and start building your team.`,
          benefits: [
            'Full agency ownership',
            'Hire and manage agents',
            'Complete lead assignment control',
            'Agency dashboard access',
            'Team analytics and reporting',
          ],
          primaryAction: 'Create Agency',
          primaryActionFn: () => setShowAgencyCreation(true),
          secondaryAction: 'Not Now',
          showAgencyCreationFlow: true,
        };
    }
  };

  const content = getModalContent();

  const handleAgencyCreate = async (agencyData: {
    name: string;
    description?: string;
    contactEmail?: string;
    role: 'agent' | 'owner';
  }) => {
    // Skip if no agency name (user clicked "Skip for Now")
    if (!agencyData.name.trim()) {
      onClose();
      return;
    }

    setIsCreatingAgency(true);

    try {
      // Prepare agency creation data
      const createData: any = {
        name: agencyData.name,
        description: agencyData.description,
        contactEmail: agencyData.contactEmail,
      };

      // If user is in an agency, create sub-agency by setting parent_agency_id
      if (userScenario === 'full_fledged_in_agency' && currentAgencyId) {
        createData.parentAgencyId = currentAgencyId;
      }

      // Create agency
      const result = await agencyService.createIndependentAgency(createData);

      // Invalidate all agency-related queries to refresh UI
      queryClient.invalidateQueries({ queryKey: AGENCY_STATUS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });

      toast({
        title: userScenario === 'full_fledged_in_agency' ? "Sub-Agency Created" : "Agency Created",
        description: userScenario === 'full_fledged_in_agency'
          ? "Your sub-agency has been created successfully. You now have access to all features."
          : "Your agency has been created successfully. You can now access all features.",
      });

      // Close modal and allow user to access the feature
      onClose();

      // Force page refresh to update permissions.
      // safeReload: same reasoning as the invite banner — user-triggered, but the
      // reload itself must not land in the middle of a live call.
      safeReload('agency-created');
    } catch (error: any) {
      toast({
        title: "Agency Creation Failed",
        description: error.message || "Failed to create agency. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingAgency(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <ResponsiveDialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
        {!showAgencyCreation ? (
          <>
            <DialogHeader>
              <div className="flex flex-col items-center text-center space-y-4">
                <div className={`w-20 h-20 ${content.iconBg} rounded-full flex items-center justify-center`}>
                  {content.icon}
                </div>
                <div>
                  <DialogTitle className="text-2xl">{content.title}</DialogTitle>
                  <DialogDescription className="mt-2 text-base">
                    {content.description}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/* Benefits List */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-3">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-600" />
                What You'll Get:
              </h4>
              <ul className="space-y-2">
                {content.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-0.5">
                      <Check className="h-3 w-3 text-green-600" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Current Plan Badge */}
            <div className="flex items-center justify-center">
              <div className="inline-flex items-center gap-3 bg-gray-100 rounded-full px-4 py-2">
                <span className="text-sm text-gray-600">Current Plan:</span>
                <Badge
                  variant="outline"
                  className={`font-semibold ${
                    accountType === 'super_agent'
                      ? 'bg-amber-100 text-amber-700 border-amber-300'
                      : accountType === 'full_fledged'
                        ? 'bg-green-100 text-green-700 border-green-300'
                        : accountType === 'basic'
                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                          : accountType === 'lite'
                            ? 'bg-slate-100 text-slate-700 border-slate-300'
                          : accountType === 'enterprise'
                            ? 'bg-purple-100 text-purple-700 border-purple-300'
                            : 'bg-gray-100 text-gray-700 border-gray-300'
                  }`}
                >
                  {currentPlanLabel}
                </Badge>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={onClose}>
                {content.secondaryAction}
              </Button>
              <Button onClick={content.primaryActionFn} className="w-full sm:w-auto">
                {content.primaryAction}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-4">
            {/* Agency Creation Form */}
            <AgencyCreationStep
              selectedPlan="full_fledged"
              onBack={() => setShowAgencyCreation(false)}
              onAgencyCreate={handleAgencyCreate}
              isLoading={isCreatingAgency}
            />
          </div>
        )}
      </ResponsiveDialogContent>
    </Dialog>
  );
}
